import type {
  FirmCategoryType,
  InvestorFirmAdminRead as CrmInvestorFirm,
} from '@capital-markets-gateway/api-client-rolodex';
import { Banner, ServerErrors } from '@cmg/common';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LoadingButton,
  Stack,
  Typography,
} from '@cmg/design-system';
import { identitySelectors } from '@cmg/e2e-selectors';
import type { FormikProps } from 'formik';
import { Field, Form, validateYupSchema, withFormik, yupToFormErrors } from 'formik';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import type { InjectedProps as InjectedModalProps } from 'redux-modal';
import { connectModal, hide, show } from 'redux-modal';
import * as yup from 'yup';

import type { UUID } from '../../../../../types/common';
import { LinkedStatus } from '../../../../../types/domain/entity-matcher/constants';
import type { FirmDataSource } from '../../../../../types/domain/firm/constants';
import type { Firm } from '../../../../../types/domain/firm/Firm';
import type { FirmMatchN1MappingData } from '../../../../../types/domain/firm-match/FirmMatchN1MappingData';
import { fetchMetadata, selectMetadata, selectMetadataLoading } from '../../../shared/ducks';
import type { updateCrmInvestorFirmLinkAction } from '../../ducks';
import {
  fetchLinkTargetFirm,
  resetLinkOrganizationModalState,
  selectLinkTargetFirm,
  selectLinkTargetFirmError,
  selectLinkTargetFirmLoading,
  selectUpdateCrmInvestorFirmLinkError,
  selectUpdateCrmInvestorFirmLinkLoading,
  updateCrmInvestorFirmLink,
} from '../../ducks';
import { LinkOrganizationEntityTypeSection } from './LinkOrganizationEntityTypeSection';
import { LinkOrganizationHeaderWarnings } from './LinkOrganizationHeaderWarnings';
import { LinkOrganizationMappingComparison } from './LinkOrganizationMappingComparison';
import { LinkOrganizationModalSkeleton } from './LinkOrganizationModalSkeleton';
import * as utils from './utils';

const getLinkOrganizationModalWithFormikSchema = (isEntityTypeRequired: boolean) => {
  return yup.object().shape({
    ...(isEntityTypeRequired
      ? {
          entityType: yup.string().nullable().required('An Entity Type is required'),
        }
      : {
          entityType: yup.string().nullable(),
        }),
    industryType: yup.string().nullable(),
    addInvestorFirmNameToCMGFirm: yup.boolean().nullable(),
  });
};

const mapStateToProps = state => ({
  linkTargetFirm: selectLinkTargetFirm(state),
  linkTargetFirmLoading: selectLinkTargetFirmLoading(state),
  linkTargetFirmError: selectLinkTargetFirmError(state),
  metadata: selectMetadata(state),
  metadataLoading: selectMetadataLoading(state),
  updateCrmInvestorFirmLinkLoading: selectUpdateCrmInvestorFirmLinkLoading(state),
  updateCrmInvestorFirmLinkError: selectUpdateCrmInvestorFirmLinkError(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchMetadata,
      fetchLinkTargetFirm,
      updateCrmInvestorFirmLink,
      resetLinkOrganizationModalState,
    },
    dispatch
  ),
});

export type Values = {
  entityType: string | null;
  industryType: string | null;
  firmType: FirmCategoryType | null;
  addInvestorFirmNameToCMGFirm: boolean;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type OwnProps = {
  /** The ID of the CRM Integration that the Investor Firm belongs to. */
  crmIntegrationId: string;
  /** The CRM Investor Firm being linked */
  crmInvestorFirm: CrmInvestorFirm;
} & (
  | {
      /** The ID of the Firm the CRM Investor Firm is to be linked to (either a CMG Firm or FactSet ID). */
      linkTargetFirmId: UUID;
      /** The source of the Firm to be linked (either CMG or FactSet) */
      linkTargetFirmSource: FirmDataSource;
      /** N:1: other CRM firms already linked to the target CMG */
      nToOneLinkedCrmFirms?: FirmMatchN1MappingData[];
    }
  | {
      linkTargetFirmId?: null;
      linkTargetFirmSource?: null;
      nToOneLinkedCrmFirms?: FirmMatchN1MappingData[];
    }
);

export type OuterProps = OwnProps & StateProps & DispatchProps & InjectedModalProps;
export type Props = OuterProps & FormikProps<Values>;

export const validate = async (values: Values, { linkTargetFirm }: OuterProps) => {
  // If the link target firm's entity type isn't set,
  // a value for entity type must be supplied - so this
  // field should be required.
  const isEntityTypeRequired = !!linkTargetFirm && !linkTargetFirm.details.entityType;
  try {
    const validationSchema = getLinkOrganizationModalWithFormikSchema(isEntityTypeRequired);
    await validateYupSchema(values, validationSchema, true);
  } catch (err) {
    return yupToFormErrors(err);
  }
  return {};
};

/**
 * A modal that allows management of CRM Investor Firm
 * links to CMG Firms
 */
export const LinkOrganizationModalComponent: React.FC<Props> = ({
  show,
  handleHide,
  crmInvestorFirm,
  linkTargetFirm,
  linkTargetFirmLoading,
  linkTargetFirmError,
  linkTargetFirmId,
  linkTargetFirmSource,
  nToOneLinkedCrmFirms = [],
  isValid,
  metadata,
  metadataLoading,
  updateCrmInvestorFirmLinkLoading,
  updateCrmInvestorFirmLinkError,
  actions,
  setFieldValue,
}) => {
  React.useEffect(() => {
    if (!metadata.entityTypes.length || !metadata.industryTypes.length) actions.fetchMetadata();

    return () => {
      actions.resetLinkOrganizationModalState();
    };
  }, [actions, metadata.entityTypes, metadata.industryTypes]);

  React.useEffect(() => {
    if (linkTargetFirmId && linkTargetFirmSource) {
      actions.fetchLinkTargetFirm({ linkTargetFirmId, linkTargetFirmSource });
    }
  }, [actions, linkTargetFirmId, linkTargetFirmSource]);

  const isInvestorFirmLinked =
    crmInvestorFirm && crmInvestorFirm.linkedStatus === LinkedStatus.LINKED;

  const linkTargetDisplayName = linkTargetFirm ? utils.getDisplayName(linkTargetFirm) : null;
  const firm = linkTargetFirm as Firm;
  const existingCmgFirmName = crmInvestorFirm?.rolodexFirmName || null;

  const otherCrmFirmsLinkedToTarget = React.useMemo(
    () => (nToOneLinkedCrmFirms || []).filter(f => f.id !== crmInvestorFirm.id),
    [nToOneLinkedCrmFirms, crmInvestorFirm.id]
  );

  return (
    <Dialog open={show} onClose={handleHide} fullWidth maxWidth="md">
      {linkTargetFirmLoading ? (
        <LinkOrganizationModalSkeleton />
      ) : (
        <React.Fragment>
          <DialogTitle>Review CRM Organization Link</DialogTitle>
          <DialogContent dividers>
            <Stack gap={2}>
              <LinkOrganizationHeaderWarnings
                firm={firm || null}
                crmInvestorFirm={crmInvestorFirm}
                linkTargetDisplayName={linkTargetDisplayName}
                otherCrmFirmsLinkedToTarget={otherCrmFirmsLinkedToTarget}
              />

              {(linkTargetFirmError || updateCrmInvestorFirmLinkError) && (
                <Banner variant="error" showIcon={false}>
                  {updateCrmInvestorFirmLinkError && (
                    <ServerErrors error={updateCrmInvestorFirmLinkError} />
                  )}
                  {linkTargetFirmError && <ServerErrors error={linkTargetFirmError} />}
                </Banner>
              )}

              <Typography>
                This action will {isInvestorFirmLinked ? 'unlink' : 'link'}{' '}
                <Typography component="span" fontWeight="bold">
                  {crmInvestorFirm.name}
                </Typography>
                {isInvestorFirmLinked && existingCmgFirmName ? (
                  <React.Fragment>
                    {' '}
                    from{' '}
                    <Typography component="span" fontWeight="bold">
                      {existingCmgFirmName}
                    </Typography>{' '}
                    and create new link with{' '}
                    <Typography component="span" fontWeight="bold">
                      {linkTargetDisplayName}
                    </Typography>
                    .
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {' '}
                    to{' '}
                    <Typography component="span" fontWeight="bold">
                      {linkTargetDisplayName}
                    </Typography>
                    .
                  </React.Fragment>
                )}
              </Typography>

              <Form id="link-org-form">
                <LinkOrganizationMappingComparison
                  crmInvestorFirm={crmInvestorFirm}
                  existingCmgFirmName={existingCmgFirmName}
                  linkTargetDisplayName={linkTargetDisplayName}
                  isInvestorFirmLinked={isInvestorFirmLinked}
                  otherCrmFirmsLinkedToTarget={otherCrmFirmsLinkedToTarget}
                />

                <LinkOrganizationEntityTypeSection
                  linkTargetFirm={firm || null}
                  linkTargetDisplayName={linkTargetDisplayName}
                  metadata={metadata}
                  metadataLoading={metadataLoading}
                  setFieldValue={setFieldValue}
                />

                {linkTargetFirm &&
                  !utils.doesInvestorFirmNameExistInLinkTargetFirm(
                    crmInvestorFirm,
                    linkTargetFirm
                  ) && (
                    <Box mt={1}>
                      <Field name="addInvestorFirmNameToCMGFirm">
                        {({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value || false}
                                onChange={event => {
                                  void setFieldValue(
                                    'addInvestorFirmNameToCMGFirm',
                                    event.target.checked
                                  );
                                }}
                              />
                            }
                            label={
                              <Typography variant="body1">
                                {crmInvestorFirm.name} does not exist as a name record in{' '}
                                {linkTargetDisplayName}. Would you like to add it?
                              </Typography>
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  )}
              </Form>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleHide}
              disabled={updateCrmInvestorFirmLinkLoading}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              form="link-org-form"
              disabled={!isValid || updateCrmInvestorFirmLinkLoading}
              loading={updateCrmInvestorFirmLinkLoading}
              data-testid={
                identitySelectors.rolodex.crmMatchListLinkOrganizationModalLinkButton.testId
              }
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </React.Fragment>
      )}
    </Dialog>
  );
};

export const LinkOrganizationModalWithFormik = withFormik<OuterProps, Values>({
  enableReinitialize: true,
  // If a link target firm is provided and the link target firm does
  // not have an entity type, the form is invalid initially.
  // Otherwise, it is valid.
  isInitialValid: ({ linkTargetFirm }) =>
    linkTargetFirm ? !!linkTargetFirm.details.entityType : true,
  handleSubmit: (values, formikBag) => {
    const payload: updateCrmInvestorFirmLinkAction['payload'] = {
      crmIntegrationId: formikBag.props.crmIntegrationId,
      crmInvestorFirmId: formikBag.props.crmInvestorFirm.id,
      ...(formikBag.props.linkTargetFirmId && formikBag.props.linkTargetFirmSource
        ? {
            linkTargetFirmId: formikBag.props.linkTargetFirmId,
            linkTargetFirmSource: formikBag.props.linkTargetFirmSource,
          }
        : {}),
      addInvestorRoleToCMGFirm: formikBag.props.linkTargetFirm
        ? !utils.doesFirmHaveInvestorRole(formikBag.props.linkTargetFirm)
        : false,
      addInvestorFirmNameToCMGFirmValue:
        values.addInvestorFirmNameToCMGFirm && formikBag.props.crmInvestorFirm
          ? formikBag.props.crmInvestorFirm.name
          : null,
      // Detect if the entity type and industry type values have changed.
      // If not, they should be null.
      firmType:
        formikBag.props.linkTargetFirm &&
        formikBag.props.linkTargetFirm.details.firmCategoryType === values.entityType
          ? null
          : values.firmType,
      entityType:
        formikBag.props.linkTargetFirm &&
        formikBag.props.linkTargetFirm.details.entityType === values.entityType
          ? null
          : values.entityType,
      industryType:
        formikBag.props.linkTargetFirm &&
        formikBag.props.linkTargetFirm.details.industryType === values.industryType
          ? null
          : values.industryType,
    };

    formikBag.props.actions.updateCrmInvestorFirmLink(payload);
  },
  mapPropsToValues: ({ linkTargetFirm, crmInvestorFirm }) => {
    // Default addInvestorFirmNameToCMGFirm to true if
    // the option to add the investor firm name exists.
    const addInvestorFirmNameToCMGFirm =
      !!crmInvestorFirm &&
      !!linkTargetFirm &&
      !utils.doesInvestorFirmNameExistInLinkTargetFirm(crmInvestorFirm, linkTargetFirm);

    return {
      entityType: linkTargetFirm ? linkTargetFirm.details.entityType : null,
      industryType: linkTargetFirm ? linkTargetFirm.details.industryType : null,
      firmType: linkTargetFirm ? linkTargetFirm.details.firmCategoryType : null,
      addInvestorFirmNameToCMGFirm,
    };
  },
  validate,
})(LinkOrganizationModalComponent);

const name = 'ROLODEX/CRM_INVESTOR_FIRM_DETAILS/LINK_ORGANIZATION_MODAL';

export const openLinkOrganizationModal = (
  params?:
    | {
        linkTargetFirmId: UUID;
        linkTargetFirmSource: FirmDataSource;
        nToOneLinkedCrmFirms?: FirmMatchN1MappingData[];
      }
    | {
        linkTargetFirmId?: null;
        linkTargetFirmSource?: null;
        nToOneLinkedCrmFirms?: FirmMatchN1MappingData[];
      }
) => show(name, params);

export const closeLinkOrganizationModal = () => hide(name);

const LinkOrganizationModal = connectModal({ name })(LinkOrganizationModalWithFormik);

export default connect<
  StateProps,
  DispatchProps,
  { crmIntegrationId: UUID; crmInvestorFirm: CrmInvestorFirm }
>(
  mapStateToProps,
  mapDispatchToProps
)(LinkOrganizationModal);
